// Standalone Sanctions Checks screen — cross-deal / cross-party
// history browser. Filter by status / subject type / name. Each row
// links back to its source deal when set.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const C = { ink: '#1e293b', muted: '#64748b', border: '#e2e8f0', soft: '#f8fafc', burgundy: '#800020' };

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '28px 40px', minHeight: 0 },
  h1:      { fontSize: 24, fontWeight: 700, color: C.ink, margin: '0 0 4px' },
  sub:     { color: C.muted, margin: '0 0 20px', fontSize: 13, maxWidth: 720 },
  toolbar: { display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' },
  select:  { padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff' },
  input:   { padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', width: 220 },
  ghost:   { padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  table:   { width: '100%', borderCollapse: 'collapse', background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', fontSize: 13 },
  th:      { textAlign: 'left', padding: '12px 14px', background: C.soft, color: C.muted, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` },
  td:      { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', color: C.ink },
  pill:    (status) => ({
    display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
    background: status === 'clear' ? '#dcfce7' : status === 'flagged' ? '#fee2e2' : status === 'error' ? '#fef2f2' : '#fef3c7',
    color:      status === 'clear' ? '#166534' : status === 'flagged' ? '#991b1b' : status === 'error' ? '#7f1d1d' : '#92400e',
  }),
  empty:   { padding: '48px 24px', textAlign: 'center', color: C.muted },
  err:     { color: '#dc2626', fontSize: 13 },
};

const STATUS_OPTIONS = ['all', 'pending', 'clear', 'flagged', 'error'];
const TYPE_OPTIONS   = ['all', 'insured', 'cedant', 'reinsurer', 'coBroker', 'retrocedent', 'other'];

const fmtDate = (v) => { if (!v) return '—'; try { return new Date(v).toISOString().slice(0, 10); } catch (e) { return String(v).slice(0, 10); } };

const SanctionsChecks = () => {
  const history = useHistory();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [status, setStatus]   = useState('all');
  const [type, setType]       = useState('all');
  const [subject, setSubject] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const q = new URLSearchParams({ limit: '200' });
      if (status !== 'all') q.set('status', status);
      if (type   !== 'all') q.set('subjectType', type);
      if (subject)          q.set('subject', subject);
      const resp = await axios.get(`${BASE_URL}/reinsurance/sanctions?${q.toString()}`, { withCredentials: true });
      setRows(resp?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load sanctions checks');
    } finally { setLoading(false); }
  }, [status, type, subject]);

  useEffect(() => { load(); }, [load]);

  return (
    <AppLayout>
      <div style={S.page}>
        <h1 style={S.h1}>Sanctions Checks</h1>
        <p style={S.sub}>Screening history across every party the tenant has checked. Manual attestations + external-provider results share the same evidence trail — regulated brokers use this as their compliance record.</p>

        <div style={S.toolbar}>
          <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>)}
          </select>
          <select style={S.select} value={type} onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t === 'all' ? 'All subjects' : t}</option>)}
          </select>
          <input style={S.input} placeholder='Subject name…' value={subject} onChange={(e) => setSubject(e.target.value)} />
          <button style={S.ghost} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          <div style={{ flex: 1 }} />
          {error && <span style={S.err}>{error}</span>}
        </div>

        {!loading && rows.length === 0 ? (
          <div style={{ ...S.table }}><div style={S.empty}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>No sanctions checks yet</div>
            <div style={{ marginTop: 6 }}>Open a deal to screen its parties (insured, cedant, reinsurers).</div>
          </div></div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Subject</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>Deal</th>
                <th style={S.th}>Provider</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Hits</th>
                <th style={S.th}>Screened</th>
                <th style={S.th}>Expires</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r._id}
                  style={{ cursor: r.deal ? 'pointer' : 'default' }}
                  onClick={() => { if (r.deal) history.push(`/reinsurance/deals/${r.deal}`); }}
                  onMouseEnter={(e) => { if (r.deal) e.currentTarget.style.background = C.soft; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ ...S.td, fontWeight: 600 }}>{r.subjectName}</td>
                  <td style={S.td}>{r.subjectType}</td>
                  <td style={S.td}>{r.dealNumber || '—'}</td>
                  <td style={S.td}>{r.provider}</td>
                  <td style={S.td}><span style={S.pill(r.status)}>{r.status}</span></td>
                  <td style={S.td}>{(r.hits || []).length}</td>
                  <td style={S.td}>{fmtDate(r.screenedAt)}</td>
                  <td style={S.td}>{fmtDate(r.expiresAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
};

export default SanctionsChecks;
